/* eslint-disable no-empty */
// AvatarCropper.jsx — Android first-load crash fix

import React, { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Cropper from "react-easy-crop";

/* ---------------- Segédfüggvények ---------------- */

// Biztos reset (Androidon különben néha nem lő új change eseményt)
function resetInputLater(inputEl) {
  requestAnimationFrame(() => {
    try {
      if (inputEl) inputEl.value = "";
    } catch {}
  });
}

// Gyors, memóriabarát normalizálás: File(Blob) → createImageBitmap → canvas → KISEBB JPEG DataURL
async function fileToNormalizedDataURL(file, maxSide = 1600, quality = 0.86) {
  // 1) Első próbálkozás: createImageBitmap a Blob-on (EXIF orientációt kezeli)
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const w = bitmap.width;
    const h = bitmap.height;
    if (!w || !h) throw new Error("bitmap-dimensions-missing");

    const scale = Math.min(1, maxSide / Math.max(w, h));
    const outW = Math.max(1, Math.round(w * scale));
    const outH = Math.max(1, Math.round(h * scale));

    const canvas = document.createElement("canvas");
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, outW, outH);

    return canvas.toDataURL("image/jpeg", quality);
  } catch (e) {
    // 2) Fallback: ObjectURL + <img> + canvas, továbbra is KISEBB méretre skálázva
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      const decoded = new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });
      img.src = url;

      if ("decode" in img) {
        try {
          await img.decode();
        } catch {
          await decoded;
        }
      } else {
        await decoded;
      }

      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      if (!w || !h) throw new Error("img-dimensions-missing");

      const scale = Math.min(1, maxSide / Math.max(w, h));
      const outW = Math.max(1, Math.round(w * scale));
      const outH = Math.max(1, Math.round(h * scale));

      const canvas = document.createElement("canvas");
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, outW, outH);

      return canvas.toDataURL("image/jpeg", quality);
    } finally {
      try {
        URL.revokeObjectURL(url);
      } catch {}
    }
  }
}

// URL/DataURL → kivágás → JPEG Blob
async function cropUrlToJpegBlob(urlOrDataUrl, cropPixels) {
  const img = new Image();
  const decoded = new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
  });
  img.src = urlOrDataUrl;

  if (img.decode) {
    try {
      await img.decode();
    } catch {
      await decoded;
    }
  } else {
    await decoded;
  }

  const canvas = document.createElement("canvas");
  const w = Math.max(1, Math.round(cropPixels.width));
  const h = Math.max(1, Math.round(cropPixels.height));
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");

  ctx.drawImage(img, cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height, 0, 0, w, h);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.9);
  });
}

const uniqueSuffix = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const baseFromName = (name) => (name ? name.replace(/\.[^.]+$/, "") : "avatar");

// Csak ha EGYÉRTELMŰEN HEIC/HEIF, dobjuk — különben megpróbáljuk dekódolni
const isExplicitHeicLike = (file) => {
  const t = (file?.type || "").toLowerCase();
  const n = (file?.name || "").toLowerCase();
  return /image\/hei(c|f)/.test(t) || /\.(heic|heif)$/.test(n);
};

/* ---------------------- Komponens ---------------------- */

export default function AvatarCropper({ onDone, initialImage = null, handleClose, onError }) {
  const { t } = useTranslation();
  const [imageSrc, setImageSrc] = useState(initialImage);
  const [zoom, setZoom] = useState(1);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showCameraWarning, setShowCameraWarning] = useState(false);

  const originalFileRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const galleryClickTimeRef = useRef(0);

  const onCropComplete = useCallback((_, pixels) => setCroppedAreaPixels(pixels), []);

  const safeNotifyError = (msg) => {
    if (onError) onError(msg);
    else alert(msg);
  };

  // Kamera figyelmeztetés automatikus elrejtése
  useEffect(() => {
    if (showCameraWarning) {
      const timer = setTimeout(() => setShowCameraWarning(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showCameraWarning]);

  const handlePickedFile = async (file, inputEl, source = "unknown") => {
    if (!file) {
      resetInputLater(inputEl);
      if (source === "camera-from-gallery") {
        setShowCameraWarning(false);
        safeNotifyError(t('settings.avatarCropper.useCameraButton'));
      }
      return;
    }

    // 0B/invalid guard (gyakori Android bug)
    if (!file.name && !file.type && file.size === 0) {
      setShowCameraWarning(false);
      safeNotifyError(t('settings.avatarCropper.useCameraButton'));
      resetInputLater(inputEl);
      return;
    }

    // HEIC/HEIF kizárás
    if (isExplicitHeicLike(file)) {
      safeNotifyError(t('settings.avatarCropper.heicNotSupported'));
      resetInputLater(inputEl);
      return;
    }

    try {
      // 1) Kíméletes normalizálás (kisebb oldalhossz)
      const normalized = await fileToNormalizedDataURL(file, 1600, 0.86);

      // 2) Cropper induló állapot visszaállítás
      originalFileRef.current = file;
      setImageSrc(normalized);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
      setCroppedAreaPixels(null);
      setShowCameraWarning(false);
    } catch (error) {
      console.error("Error processing file:", error);
      setShowCameraWarning(false);
      safeNotifyError(t('settings.avatarCropper.imageOpenError'));
    } finally {
      resetInputLater(inputEl);
    }
  };

  // Galéria fájl kezelés
  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await handlePickedFile(file, e.target, "gallery");
    } else {
      setShowCameraWarning(false);
      resetInputLater(e.target);
    }
  };

  // Kamera fájl kezelés
  const onCameraChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await handlePickedFile(file, e.target, "camera");
    } else {
      resetInputLater(e.target);
    }
  };

  // Galéria gomb kattintás - dupla kattintás védelem
  const handleGalleryClick = () => {
    const now = Date.now();
    if (now - galleryClickTimeRef.current < 500) return;
    galleryClickTimeRef.current = now;

    if (fileInputRef.current) fileInputRef.current.value = "";
    setTimeout(() => fileInputRef.current?.click(), 100);
  };

  // Kamera gomb
  const handleCameraClick = () => {
    if (cameraInputRef.current) cameraInputRef.current.value = "";
    setTimeout(() => cameraInputRef.current?.click(), 100);
  };

  // "Choose a picture" gomb — figyelmeztetés kamerára
  const handleChoosePictureClick = () => {
    setShowCameraWarning(true);
    if (fileInputRef.current) fileInputRef.current.dataset.source = "gallery-with-camera-warning";
    handleGalleryClick();
  };

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setSaving(true);
    try {
      const blob = await cropUrlToJpegBlob(imageSrc, croppedAreaPixels);
      const orig = originalFileRef.current?.name || "avatar";
      const name = `${baseFromName(orig)}-${uniqueSuffix()}.jpg`;
      const file = new File([blob], name, { type: "image/jpeg", lastModified: Date.now() });
      onDone?.(file);
    } catch (error) {
      console.error("Save error:", error);
      safeNotifyError(t('settings.avatarCropper.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="avatar-cropper">
      {!imageSrc && (
        <div className="picker-div" style={{ display: "grid", gap: 16, justifyItems: "center", padding: "20px" }}>
          {/* Kamera figyelmeztetés */}
          {showCameraWarning && (
            <div
              style={{
                backgroundColor: "#fff3cd",
                color: "#856404",
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid #ffeaa7",
                fontSize: "14px",
                textAlign: "center",
                maxWidth: "300px",
              }}
            >
              💡
              {t('settings.avatarCropper.cameraTip')}
            </div>
          )}

          {/* GALÉRIA gomb */}
          <div style={{ display: "grid", gap: 8, justifyItems: "center" }}>
            <button
              type="button"
              className="button primary"
              onClick={handleChoosePictureClick}
              style={{ padding: "12px 24px", fontSize: "16px", minWidth: "200px" }}
            >
              {t('settings.avatarCropper.choosePicture')}
            </button>

            {/* Rejtett file input a galériához */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={onFileChange}
              style={{ display: "none" }}
              multiple={false}
            />
          </div>

          <div style={{ color: "#666", fontSize: "14px" }}>{t('settings.avatarCropper.or')}</div>

          {/* KAMERA gomb */}
          <div style={{ display: "grid", gap: 8, justifyItems: "center" }}>
            <button
              type="button"
              className="button primary"
              onClick={handleCameraClick}
              style={{
                padding: "12px 24px",
                fontSize: "16px",
                minWidth: "200px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              📸 {t('settings.avatarCropper.takePhoto')}
            </button>

            {/* Rejtett camera input */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={onCameraChange}
              style={{ display: "none" }}
              multiple={false}
            />
          </div>
        </div>
      )}

      {imageSrc && (
        <>
          <div className="crop-area" style={{ position: "relative", width: "100%", height: 320 }}>
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              restrictPosition
              cropShape="round"
              showGrid={false}
            />
          </div>

          <div className="controls" style={{ display: "grid", gap: 12, marginTop: 12 }}>
            <label style={{ display: "grid", gap: 6 }}>
              {t('settings.avatarCropper.zoom')}
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                style={{ width: "100%" }}
              />
            </label>

            <div className="buttons" style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
              <button
                type="button"
                className="button secondary"
                onClick={handleGalleryClick}
                disabled={saving}
                style={{ minWidth: "120px" }}
              >
                {t('settings.avatarCropper.anotherPicture')}
              </button>

              <button className="button primary" onClick={handleSave} disabled={saving} style={{ minWidth: "120px" }}>
                {saving ? t('settings.avatarCropper.saving') : t('settings.avatarCropper.save')}
              </button>

              {handleClose && (
                <button
                  className="button outline"
                  onClick={handleClose}
                  disabled={saving}
                  style={{ minWidth: "120px" }}
                >
                  {t('settings.avatarCropper.cancel')}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
